import { Option } from '@joor/design-system'
import { isEmpty } from 'lodash'

import {
  BillingAddress,
  BillingInfo,
  OrderBilling,
} from '__generated__/atlas-types'

import { RetailerLocation } from '../../EditAddressesModal/hooks/useAddressModalData'
import { ShippingAddressFormValues } from '../ShippingAddressForm/shippingAddressForm.types'
import {
  GetAddressOptionsInputType,
  OptionsRecord,
  cleanupLocationId,
  extractCompanyFromLabel,
  formatBrandAddressOptions,
  formatOrderAddressOption,
  formatPrimaryLocationOption,
  formatRetailerLocationOptions,
  getAddressOptionsForBrands,
  getAddressOptionsForLiteRetailers,
  getCountryOption,
} from '../forms.utils'
import { BillingAddressFormValues, FieldIds } from './billingAddressForm.types'
import { isOrderAddressIncluded } from 'features/Orders/Review/components/Overview/components/AddressesWidget/addressesWidget.utils'

type FormatterToFormValuesType = {
  selected: Option | null
  countryOption: Option | undefined
  taxNumber: string | undefined | null
  paymentMethodOption?: Option | null
}

export type InitialBillingFormValuesInput = {
  orderBilling: OrderBilling | undefined
  countryOptions: Option[]
  billingAddressOptions: Option[]
  taxNumber: string | undefined | null
}

export const getInitialBillingFormValues = ({
  orderBilling,
  countryOptions,
  billingAddressOptions,
  taxNumber,
}: InitialBillingFormValuesInput): BillingAddressFormValues => {
  const foundSelected = billingAddressOptions.find(
    (address) =>
      extractCompanyFromLabel(address.label) === orderBilling?.address.company,
  )

  return formatOrderBillingToFormValues({
    taxNumber,
    selected: foundSelected?.label
      ? {
          ...foundSelected,
          label: extractCompanyFromLabel(foundSelected.label),
        }
      : null,
    countryOption: getCountryOption(
      orderBilling?.address.country,
      countryOptions,
    ),
    orderBilling,
  })
}

export const formatBillingAddressToFormValues = ({
  selected,
  billingAddress,
  countryOption,
  taxNumber,
  paymentMethodOption,
}: FormatterToFormValuesType & {
  billingAddress: BillingAddress | undefined
}): BillingAddressFormValues => {
  const { address, code, phoneNumber } = billingAddress ?? {}
  return {
    [FieldIds.AddressOption]: selected,
    [FieldIds.CompanyName]: address?.companyName ?? '',
    [FieldIds.RecipientName]: address?.name ?? '',
    [FieldIds.Address1]: address?.line1 ?? '',
    [FieldIds.Address2]: address?.line2 ?? '',
    [FieldIds.City]: address?.city ?? '',
    [FieldIds.Territory]: address?.state ?? '',
    [FieldIds.Country]: countryOption ?? null,
    [FieldIds.TaxNumber]: taxNumber ?? '',
    [FieldIds.PostalCode]: address?.zip ?? '',
    [FieldIds.BillingCode]: code ?? '',
    [FieldIds.Phone]: phoneNumber ?? '',
    [FieldIds.PaymentMethod]: paymentMethodOption ?? null,
  }
}

/**
 * Only used when "isSameAddressForBillingAndShipping" checkbox is checked.
 */
export const formatShippingFormValuesToBillingFormValues = (
  shippingFormValues: ShippingAddressFormValues,
): BillingAddressFormValues => ({
  [FieldIds.AddressOption]: shippingFormValues?.addressOption
    ? { label: shippingFormValues?.addressOption?.label, value: '' }
    : null,
  [FieldIds.CompanyName]: shippingFormValues?.companyName ?? '',
  [FieldIds.RecipientName]: shippingFormValues?.recipientName ?? '',
  [FieldIds.Address1]: shippingFormValues?.address1 ?? '',
  [FieldIds.Address2]: shippingFormValues?.address2 ?? '',
  [FieldIds.City]: shippingFormValues?.city ?? '',
  [FieldIds.Territory]: shippingFormValues?.territory ?? '',
  [FieldIds.Country]: shippingFormValues?.country ?? null,
  [FieldIds.TaxNumber]: '',
  [FieldIds.PostalCode]: shippingFormValues?.postalCode ?? '',
  [FieldIds.BillingCode]: '',
  [FieldIds.Phone]: shippingFormValues?.phone ?? '',
})

export const formatOrderBillingToFormValues = ({
  selected,
  orderBilling,
  countryOption,
  taxNumber,
  paymentMethodOption,
}: FormatterToFormValuesType & {
  orderBilling: OrderBilling | undefined
}): BillingAddressFormValues => ({
  [FieldIds.AddressOption]: selected,
  [FieldIds.CompanyName]: orderBilling?.address.company ?? '',
  [FieldIds.RecipientName]: orderBilling?.address.name ?? '',
  [FieldIds.Address1]: orderBilling?.address.firstAddress ?? '',
  [FieldIds.Address2]: orderBilling?.address.secondAddress ?? '',
  [FieldIds.City]: orderBilling?.address.city ?? '',
  [FieldIds.Territory]: orderBilling?.address.state ?? '',
  [FieldIds.Country]: countryOption ?? null,
  [FieldIds.TaxNumber]: taxNumber ?? '',
  [FieldIds.PostalCode]: orderBilling?.address.zip ?? '',
  [FieldIds.BillingCode]:
    orderBilling?.address?.sourceBillingAddress?.code ?? '',
  [FieldIds.Phone]: orderBilling?.address.phone ?? '',
  [FieldIds.PaymentMethod]: paymentMethodOption ?? null,
})

export const formatRetailerLocationToFormValues = ({
  selected,
  retailerLocation,
  countryOption,
  taxNumber,
  paymentMethodOption,
}: FormatterToFormValuesType & {
  retailerLocation: RetailerLocation | undefined
}): BillingAddressFormValues => ({
  [FieldIds.AddressOption]: selected,
  [FieldIds.CompanyName]: retailerLocation?.name ?? '',
  // we don't have a recipient name for the primary location yet
  // even we send it empty, the response back has the source address name
  [FieldIds.RecipientName]: '',
  [FieldIds.Address1]: retailerLocation?.address1 ?? '',
  [FieldIds.Address2]: retailerLocation?.address2 ?? '',
  [FieldIds.City]: retailerLocation?.city ?? '',
  [FieldIds.Territory]: retailerLocation?.state ?? '',
  [FieldIds.Country]: countryOption ?? null,
  [FieldIds.TaxNumber]: taxNumber ?? '',
  [FieldIds.PostalCode]: retailerLocation?.zip ?? '',
  [FieldIds.BillingCode]: '',
  [FieldIds.Phone]: retailerLocation?.phone ?? '',
  [FieldIds.PaymentMethod]: paymentMethodOption ?? null,
})

export const getBillingAddressOptions = ({
  billingAddresses,
  primaryLocation,
  retailerLocations,
  billing,
  isAccountTypeBrand,
}: {
  billingAddresses: BillingAddress[]
  primaryLocation: RetailerLocation | undefined
  retailerLocations?: RetailerLocation[]
  billing: OrderBilling | undefined
  isAccountTypeBrand?: boolean
}): OptionsRecord => {
  const brandAddresses = billingAddresses.map(({ address }) => address)
  const orderAddress = billing?.address

  const addressOptions: GetAddressOptionsInputType = {
    orderAddressOption: formatOrderAddressOption(orderAddress),
    primaryLocationOption: formatPrimaryLocationOption(primaryLocation),
    retailerLocationsOptions: formatRetailerLocationOptions(
      retailerLocations ?? [],
    ),
    brandAddressOptions: formatBrandAddressOptions(billingAddresses),
    shouldAddOrderAddress:
      !isEmpty(orderAddress) &&
      !isOrderAddressIncluded(brandAddresses, orderAddress),
  }

  return isAccountTypeBrand
    ? getAddressOptionsForBrands(addressOptions)
    : getAddressOptionsForLiteRetailers(addressOptions)
}

export const formatBillingFormValuesToBillingInfo = (
  formValues: BillingAddressFormValues,
): BillingInfo => ({
  billingAddressId: cleanupLocationId(formValues[FieldIds.AddressOption]),
  address: {
    // this value is required to have value by the mutation, but it might come as null from the BE query
    // that's why we send an empty string in that case
    companyName: formValues[FieldIds.CompanyName] ?? '',
    recipientName: formValues[FieldIds.RecipientName],
    city: formValues[FieldIds.City],
    country: formValues[FieldIds.Country]?.label,
    line1: formValues[FieldIds.Address1],
    line2: formValues[FieldIds.Address2],
    state: formValues[FieldIds.Territory],
    zip: formValues[FieldIds.PostalCode],
  },
  taxIdNumber: formValues[FieldIds.TaxNumber],
  phoneNumber: formValues[FieldIds.Phone],
  ...(formValues[FieldIds.PaymentMethod]?.value && {
    paymentMethodId: formValues[FieldIds.PaymentMethod]?.value,
  }),
})
